import {
    Component,
    input,
    signal,
    inject,
    effect,
    untracked,
    output,
    viewChild,
    ChangeDetectionStrategy,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { S25TableModule } from "../../../s25-table/s25.table.module";
import { S25TableComponent } from "../../../s25-table/s25.table.component";
import { Table } from "../../../s25-table/Table";
import { S25Util } from "../../../../util/s25-util";
import { Bind } from "../../../../decorators/bind.decorator";
import { BudgetService } from "../budget.service";
import { BudgetCreateModalComponent } from "./budget.create.modal.component";
import { BudgetEditModalComponent } from "./budget.edit.modal.component";
import { GenericTableActionsComponent, TableAction } from "../../../s25-table/generics/generic.table.actions.component";
import { CacheRepository } from "../../../../decorators/cache.decorator";
import { UserprefService } from "../../../../services/userpref.service";
import { S25Datefilter } from "../../../s25-dateformat/s25.datefilter.service";
import { SeSecurityStore } from "../../se.security.store";
import { S25RouterLinkComponent } from "../../../s25-nav-bar/s25.router.link.component";
import { S25States } from "../../../../services/state/state.map";
import { Budget } from "../Budget";
import { BudgetLabelPhrases, BudgetLabels } from "../budget.labels";

@TypeManagerDecorator("s25-ng-budget-admin-organizations")
@Component({
    selector: "s25-ng-budget-admin-organizations",
    template: `
        <div class="organizations-tab">
            <div class="section-header">
                <h3>{{ labels.ORGANIZATION_BUDGETS }} - {{ selectedFiscalYear() }}</h3>

                @if (securityStore.hasFLSOverride()) {
                    <s25-ng-budget-create-modal (budgetCreated)="handleBudgetCreated()"></s25-ng-budget-create-modal>
                }
            </div>

            @if (errorMsg()) {
                <div class="alert alert-danger" role="alert">
                    {{ errorMsg() }}
                </div>
            }
            <s25-ng-table
                class="c-margin-left--single c-margin-top--single c-margin-right--single"
                [dataSource]="organizationTableData"
                [hasRefresh]="true"
                [hasFilter]="true"
                [columnSortable]="true"
                [hasColumnChooser]="true"
                [hasTotalRowCount]="true"
                [caption]="labels.ORGANIZATION_BUDGET_STATUS"
                [noResultsMessage]="'No organizations found'"
            ></s25-ng-table>

            <s25-ng-budget-edit-modal
                #budgetEditModal
                (budgetUpdated)="handleBudgetUpdated()"
            ></s25-ng-budget-edit-modal>
        </div>
    `,
    styles: `
        .organizations-tab {
            background: var(--color--surface-bg);
            border-radius: 8px;
            box-shadow: 0 2px 8px color-mix(in srgb, var(--color--text) 12%, transparent);
            overflow: hidden;
        }

        .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.5rem;
            background: var(--color--surface-2-bg);
            border-bottom: 1px solid var(--color--border);
        }

        .section-header h3 {
            margin: 0;
            font-size: 1.2rem;
            color: var(--color--text);
            font-weight: 600;
        }

        @media (max-width: 768px) {
            .section-header {
                flex-direction: column;
                gap: 1rem;
                align-items: stretch;
            }

            .section-header s25-ng-button {
                width: 100%;
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [S25TableModule, BudgetCreateModalComponent, BudgetEditModalComponent],
})
export class BudgetAdminOrganizationsComponent {
    readonly labels = BudgetLabels;

    selectedFiscalYear = input.required<string>();
    readonly budgetChanged = output<void>();

    securityStore = inject(SeSecurityStore);
    StateNames = S25States.names;
    errorMsg = signal("");
    tableComponent = viewChild(S25TableComponent);
    budgetEditModal = viewChild(BudgetEditModalComponent);

    constructor() {
        effect(() => {
            untracked(() => {
                this.securityStore.ensureInitialized();
            });
        });
    }

    organizationTableData: Table.DataSource = {
        type: "unpaginated",
        dataSource: this.getOrganizationTableRows,
        columns: [
            {
                id: "organizationName",
                header: "Organization",
                minWidth: 100,
                cells: {
                    component: S25RouterLinkComponent,
                    className: "font-weight-medium",
                },
            },
            {
                id: "organizationType",
                header: "Type",
                cells: {
                    text: "",
                },
            },
            {
                id: "budgetStatus",
                header: BudgetLabels.BUDGET_STATUS,
                minWidth: 40,
                align: "center",
                cells: {
                    text: "",
                    className: "budget-status",
                },
            },
            {
                id: "workflow",
                header: "Workflow",
                minWidth: 140,
                cells: {
                    text: "",
                },
            },
            {
                id: "allocatedAmount",
                header: "Allocated Amount",
                align: "right",
                cells: {
                    text: "",
                    className: "currency-amount",
                },
            },
            {
                id: "utilizationPercentage",
                header: "Utilization",
                align: "center",
                cells: {
                    text: "",
                    className: "utilization-percentage",
                },
            },
            {
                id: "lastModified",
                header: "Last Modified",
                cells: {
                    text: "",
                },
            },
            {
                id: "actions",
                header: "Actions",
                width: 140,
                sortable: false,
            },
        ],
    };

    @Bind
    async getOrganizationTableRows(): Promise<Table.DataSourceResponse> {
        this.errorMsg.set("");
        const dateFormat = await UserprefService.getS25Dateformat();
        CacheRepository.invalidateByService("BudgetService", "getOrganizationBudgetStatus");
        const [budgets, error] = await S25Util.Maybe(
            BudgetService.getOrganizationBudgetStatus(this.selectedFiscalYear()),
        );

        if (error) {
            this.errorMsg.set(`${S25Util.errorText(error, "Failed to load organization budgets.")}`);
            return { rows: [], totalRows: 0 };
        }

        const rows: Table.Row[] = budgets.map((budget) => ({
            id: budget.organizationId,
            name: budget.organizationName,
            data: budget,
            cells: {
                organizationName: {
                    component: S25RouterLinkComponent,
                    inputs: {
                        text: budget.organizationName,
                        stateName: S25States.names.seOrganizationDetails,
                        stateParams: { itemId: budget.organizationId },
                    },
                    textValue: budget.organizationName,
                },
                organizationType: { text: budget.organizationType },
                budgetStatus: { text: budget.budgetStatus },
                workflow: { text: this.getWorkflowLabel(budget) },
                allocatedAmount: { text: budget.allocatedAmount },
                utilizationPercentage: {
                    text: `${(budget.utilizationPercentage ?? 0).toFixed(1)}%`,
                    className: "utilization-percentage",
                },
                lastModified: { text: S25Datefilter.transform(budget.lastModDate, dateFormat) },
                actions: {
                    component: GenericTableActionsComponent,
                    inputs: {
                        actions: this.getRowActions(budget),
                    },
                },
            },
        }));
        return { rows, totalRows: budgets.length };
    }

    async handleBudgetCreated(): Promise<void> {
        CacheRepository.invalidateByService("BudgetService", "getOrganizationBudgetStatus");
        this.budgetChanged.emit();
        await this.tableComponent()?.refresh(true);
    }

    async handleBudgetUpdated(): Promise<void> {
        CacheRepository.invalidateByService("BudgetService", "getOrganizationBudgetStatus");
        this.budgetChanged.emit();
        await this.tableComponent()?.refresh(true);
    }

    private getRowActions(budget: Budget.OrganizationBudgetStatus): TableAction[] {
        if (!this.securityStore.hasFLSOverride() || !budget.budgetId) {
            return [];
        }

        return [
            {
                label: "Edit",
                type: "outline",
                onClick: () => this.editBudget(budget),
            },
        ];
    }

    async editBudget(status: Budget.OrganizationBudgetStatus): Promise<void> {
        const [orgBudgets, error] = await S25Util.Maybe(BudgetService.getAllOrgBudgets(status.organizationId));

        if (error) {
            S25Util.showError(error, "Failed to load budget for editing.");
            return;
        }

        const budget = orgBudgets?.find((orgBudget) => orgBudget.budgetId === status.budgetId);
        if (!budget) {
            S25Util.showError(null, BudgetLabelPhrases.allocationNotFound);
            return;
        }

        await this.budgetEditModal()?.openEditModal(status.organizationId, budget);
    }

    private getWorkflowLabel(budget: Budget.OrganizationBudgetStatus): string {
        if (!budget.workflowProcessId) {
            return "Auto-approve";
        }
        return budget.workflowProcessName ?? `Workflow #${budget.workflowProcessId}`;
    }
}
